import empty from "is-empty";
import "src/styles/agGridCustom.scss";
import { ERROR_TYPE } from "./constants";
import {
    divTooltipGetter,
    callableTooltipGetter,
    valueDateTooltipGetter,
    genericValueDateTooltipGetter,
    maturityTooltipGetter,
    TooltipGetterShowOriginIfDifferent,
    qtyToolTipGetter,
    rateTooltipGetter,
    availableRateTooltipGetter,
    txCreateRateTooltipGetter,
    txTypeTooltipGetter,
    objectiveTooltipGetter,
    txCreateVDTooltipGetter,
    txQtyTooltipGetter,
    txCollateralTooltipGetter,
    txCurrencyTooltipGetter,
    offerQtyToolTipGetter,
    txRateTooltipGetter,
    confirmationTooltipGetter,
    availableQtyTooltipGetter,
    marketDataTooltipGetter,
    TooltipGetterForCompareWithOrigin,
    availabilityLoanTypeTooltipGetter,
    secretButtonTooltipGetter,
    counterpartyCodeTooltipGetter,
    listNameTooltipGetter,
    accountNameTooltipGetter,
    txSplitOfferTooltipGetter,
    inventoryUploadRowTooltipGetter,
} from "./getters";

const tooltipGetterByType = (params) => {
    if (params.type === "loanType") {
        return txTypeTooltipGetter(params);
    }
    if (params.type === "objective") {
        return objectiveTooltipGetter(params);
    }
    if (params.type === "txListQty" || params.type === "txListTruQty") {
        return txQtyTooltipGetter(params);
    }
    if (params.type === "CollateralType") {
        const tooltip = txCollateralTooltipGetter(params);
        if (tooltip != null) return tooltip;
    }
    if (params.type === "CollateralCCY") {
        const tooltip = txCurrencyTooltipGetter(params);
        if (tooltip != null) return tooltip;
    }
    if (params.type === "txListRate") {
        return txRateTooltipGetter(params);
    }
    if (params.type === "rate") {
        const tooltip = rateTooltipGetter(params);
        if (tooltip != null) return tooltip;
    }
    if (params.type === "availableRate") {
        return availableRateTooltipGetter(params);
    }
    if (params.type === "quantity") {
        const tooltip = qtyToolTipGetter(params);
        if (tooltip != null) return tooltip;
    }
    if (params.type === "offerQty" || params.type === "truQty") {
        return offerQtyToolTipGetter(params);
    }

    if (params.type === "ValueDate") {
        const tooltip = valueDateTooltipGetter(params);
        if (tooltip != null) return tooltip;
    }

    // e.g. when editing inventory
    if (params.type === "GenericValueDate") {
        const tooltip = genericValueDateTooltipGetter(params);
        if (tooltip != null) return tooltip;
    }

    if (params.type === "Maturity") {
        if (!params.data) return null;
        return maturityTooltipGetter(params);
    }

    if (params.type === "Dividend") {
        return divTooltipGetter(params);
    }

    if (params.type === "Callable") {
        const tooltip = callableTooltipGetter(params);
        if (tooltip != null) return tooltip;
    }

    if (params.type === "txCreateRate") {
        return txCreateRateTooltipGetter(params);
    }

    if (params.type === "txCreateVD") {
        return txCreateVDTooltipGetter(params);
    }

    if (params.type === "availableQty") {
        return availableQtyTooltipGetter(params);
    }

    if (params.type === "confirmation") {
        return confirmationTooltipGetter(params);
    }

    if (params.type === "MD") {
        return marketDataTooltipGetter(params);
    }

    if (params.type === "availabilityLoanType") {
        return availabilityLoanTypeTooltipGetter(params);
    }

    if (params.type === "secretButtonTooltip") {
        return secretButtonTooltipGetter();
    }

    if (params.checkOrigin) {
        return TooltipGetterForCompareWithOrigin(params);
    }

    if (params.inventoryShowOrigin) {
        return TooltipGetterShowOriginIfDifferent(params);
    }

    if (params.type === "counterpartyCode") {
        return counterpartyCodeTooltipGetter(params.value);
    }
    if (params.type === "listName") {
        return listNameTooltipGetter(params.value);
    }
    if (params.type === "accountName") {
        return accountNameTooltipGetter(params.value);
    }
    if (params.type === "txSplitOffer") {
        return txSplitOfferTooltipGetter(params);
    }
    if (params.type === "inventoryUploadRow") {
        return inventoryUploadRowTooltipGetter(params);
    }
};

export default class TruTooltip {
    init(params) {
        if (empty(params)) return;
        this.eGui = document.createElement("div");
        this.eGui.className = "custom-tooltip";
        const value =
            (params.preemptiveType &&
                tooltipGetterByType({
                    ...params,
                    type: params.preemptiveType,
                })) ||
            tooltipGetterByType(params) ||
            (params.fallbackType &&
                tooltipGetterByType({ ...params, type: params.fallbackType }));

        if (empty(value)) return;

        const { originValue, errorType, testId, tooltipMessage, allowZero } =
            value;

        // Build the tooltip via DOM. User-authored values (originValue,
        // tooltipMessage) and the hardcoded error strings all go through
        // textContent, so nothing is parsed as HTML.
        const root = document.createElement("div");
        if (originValue !== undefined) {
            const ov = document.createElement("div");
            ov.className = "originValue";
            ov.append("Original Value: ");
            const span = document.createElement("span");
            span.setAttribute("data-testid", "tooltip-origin-value");
            span.textContent = originValue;
            ov.appendChild(span);
            root.appendChild(ov);
            if (errorType) {
                const err = document.createElement("div");
                err.className = "error";
                const es = document.createElement("span");
                es.className = "errorMessage";
                es.setAttribute("data-testid", "tooltip-error-message");
                es.textContent = this.errorMessageByErrorType(errorType, allowZero);
                err.appendChild(es);
                root.appendChild(err);
            }
        } else if (errorType) {
            const es = document.createElement("span");
            es.className = "errorMessage";
            es.setAttribute("data-testid", "tooltip-error-message");
            es.textContent = this.errorMessageByErrorType(errorType, allowZero);
            root.appendChild(es);
        } else if (tooltipMessage) {
            root.setAttribute("data-testid", testId || "tooltip-message");
            // A message may carry line breaks. Each line becomes its own text
            // node with a <br> between, so the break renders while the text
            // itself is still never parsed as HTML.
            String(tooltipMessage)
                .split("\n")
                .forEach((line, idx) => {
                    if (idx > 0) root.appendChild(document.createElement("br"));
                    root.appendChild(document.createTextNode(line));
                });
        }
        this.eGui.appendChild(root);
    }

    errorMessageByErrorType(errorType, allowZero) {
        if (errorType === ERROR_TYPE.QTY_TW) {
            return "Only values to nearest thousand allowed for Taiwan stocks";
        } else if (errorType === ERROR_TYPE.RATE) {
            return "-500 ≤ Rate ≤ 500";
        } else if (errorType === ERROR_TYPE.RATE_COMPARE_OFFER) {
            return "Rate cannot be decreased to less than the original offered rate";
        } else if (errorType === ERROR_TYPE.RATE_TOO_HIGH) {
            return "Rate cannot exceed the Borrower Requested Rate";
        } else if (errorType === ERROR_TYPE.RATE_TOO_LOW) {
            return "Rate is too low compared to the Borrower Requested Rate";
        } else if (errorType === ERROR_TYPE.DIV) {
            return "0 ≤ Div ≤ 100";
        } else if (errorType === ERROR_TYPE.QTY_MISMATCH_MIN_MAX_INC) {
            return "Offer Qty is incompatible with the Borrowers Requested Qty";
        } else if (errorType === ERROR_TYPE.QTY_MISMATCH_COLLATERAL) {
            return "Collateral is incompatible with the Borrowers Requested Collateral";
        } else if (errorType === ERROR_TYPE.QTY_MISMATCH_CURRENCY) {
            return "Collateral is incompatible with the Borrowers Requested Currency";
        } else if (errorType === ERROR_TYPE.VALUE_DATE_MISMATCH) {
            return "Value Date is incompatible with the Borrowers Requested Value Date";
        } else if (errorType === ERROR_TYPE.MATURITY_DATE_MISMATCH) {
            return "Maturity Date is incompatible with the Borrowers Requested Maturity Date";
        } else if (errorType === ERROR_TYPE.DIVIDEND_NOT_EXACT_MATCH) {
            return "Dividend must match Borrowers Requested dividend";
        } else if (errorType === ERROR_TYPE.CALLABLE_INCOMPATIBLE) {
            return "Callable is incompatible with the Borrowers Requested callable value";
        } else if (errorType === ERROR_TYPE.DIVIDEND_EXCEEDS_REQUESTED) {
            return "Dividend must not exceed Borrowers Requested dividend";
        } else if (errorType === ERROR_TYPE.QTY) {
            if (allowZero) return "0 ≤ Qty ≤ 1,000,000,000";
            else return "0 < Qty ≤ 1,000,000,000";
        } else if (errorType === ERROR_TYPE.QTY_COMPARE_CONFIRM) {
            return "Quantity cannot be increased to more than the original recorded quantity";
        } else if (errorType === ERROR_TYPE.QTY_COMPARE_OFFER) {
            return "Quantity cannot be increased to more than the original offered quantity";
        } else if (errorType === ERROR_TYPE.QTY_COMPARE_REQ) {
            return "Quantity cannot be increased to more than the original requested quantity";
        } else if (errorType === ERROR_TYPE.QTY_COMPARE_ORIGIN) {
            return "Quantity cannot be increased to more than the original quantity";
        } else if (
            errorType === ERROR_TYPE.QTY_EXCEEDS_DYNAMIC_LIST_INVENTORY
        ) {
            return "Quantity exceeds available inventory for the dynamic list";
        } else if (
            errorType === ERROR_TYPE.COMBINED_QTY_EXCEEDS_DYNAMIC_LIST_INVENTORY
        ) {
            return "Combined quantity exceeds available inventory for the dynamic list";
        } else if (errorType === ERROR_TYPE.MATURITY) {
            return "Maturity date cannot be earlier than the value date";
        } else if (errorType === ERROR_TYPE.VALUE_DATE) {
            return "Empty value date not allowed";
        } else if (errorType === ERROR_TYPE.BAD_VALUE_DATE) {
            return "Value Date must be between T0 .. T5";
        } else if (errorType === ERROR_TYPE.LISTNAME) {
            return "List name is not recognised";
        } else if (errorType === ERROR_TYPE.MATURITY_MAX_EXCEED) {
            return "Exceeds Max allowable Maturity";
        } else if (errorType === ERROR_TYPE.SPLIT_NOT_ALLOWED) {
            return "Cannot split this offer";
        } else if (errorType === ERROR_TYPE.SPLIT_MAXED_OUT) {
            return "The maximum acceptable number of split offers have already been added";
        }
    }

    getGui() {
        return this.eGui;
    }
}
